// opt-in 방식 consent 배너를 노출해야 하는 지역.
// 분류 기준: 외국 중소 e-commerce에 대한 실제 집행/과징금 사례가 있거나 임박한 지역만.
// 법 텍스트만 있고 집행 전무한 지역(VN/ID/ZA/TR/SA/AE 등)은 Nike 패턴(Tier 3)으로
// fallback시켜 분석 데이터 커버리지 확보. 연 1회 재검증 필요.
// (함수명은 실무 관례상 isGdprCountry 유지 — 시장에서 "GDPR 스타일"로 통칭됨)
//
// ⚠️ 철회권(CRD Art. 11a) 판별에는 이 목록을 쓰지 말 것 — **EEA 30 외에 GB/CH/BR/CA/CN**
// 이 섞여 있고, 값이 없을 때의 폴백 방향도 반대(여기는 true, 저기는 false)다.
// (NO/IS/LI 는 #5175 에서 `EEA_COUNTRIES` 에도 포함됐으므로 더 이상 차이가 아니다.)
// 철회권은 `src/constants/eea-countries.ts` 의 `isEeaCountry` 를 쓴다.
// 두 집합을 분리해 둔 근거는 `docs/order-cancellation-policy.md` §3.1.
export const GDPR_COUNTRIES = [
  // EU 27
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE",
  // UK
  "GB",
  // EEA (non-EU)
  "NO",
  "IS",
  "LI",
  // Swiss (FADP — GDPR과 유사)
  "CH",
  // Brazil (LGPD — 집행 활발, 과징금 실사례)
  "BR",
  // Canada (퀘벡 Law 25 — GDPR 수준 opt-in 집행. Vercel 헤더가 province 구분
  // 불가하므로 퀘벡 유저 보호 위해 CA 전체를 opt-in으로 처리)
  "CA",
  // China (PIPL — 2026.1부터 CAC 적극 집행, 글로벌 매출 5% 과징금 실제 가능)
  "CN",
] as const;

// 쿠키 배너 자체가 관례상 요구되지 않는 지역. 법적으로는 개인정보처리방침 +
// opt-out 경로(Footer Cookie Settings)만 있으면 충분. 배너 없이 tracker 기본 ON.
// GPC 시그널이 있으면 지역과 무관하게 존중되므로 여기 있어도 안전.
export const NO_BANNER_COUNTRIES = [
  "KR", // 한국 PIPA — 배너 관례 없음, 정책 + 회원가입 동의로 커버
  "JP", // 일본 APPI — opt-out 중심, 배너 거의 없음
  "TW", // 대만 PDPA
  "HK", // 홍콩 PDPO
  "SG", // 싱가포르 PDPA — 배너 선택
  "MY", // 말레이시아 PDPA
  "PH", // 필리핀 DPA
  // CN, VN, ID, IN은 법적 리스크로 GDPR_COUNTRIES에 포함됨
  "AU", // 호주 Privacy Act — Privacy Policy로 충분
  "NZ", // 뉴질랜드 Privacy Act
  // CA는 퀘벡 Law 25 리스크로 GDPR_COUNTRIES에 포함됨
] as const;

/**
 * countryCode가 opt-in 배너 요구 지역(GDPR 스타일)인지 판단.
 *
 * 값이 없거나 불확실할 때는 true로 fallback — 비대칭 리스크 때문에 안전쪽.
 * - false-negative(실제 EU인데 false 리턴): GDPR 위반 → 벌금 리스크
 * - false-positive(비-EU인데 true 리턴): 더 엄격한 배너 노출 → UX 불편뿐, 법적 문제 0
 *
 * countryCode가 비어있는 케이스: 최초 요청으로 middleware가 아직 쿠키를 못 내림,
 * Vercel 이외 환경, localhost, headers 누락 등.
 */
export const isGdprCountry = (countryCode?: string | null): boolean => {
  if (!countryCode) return true;
  return (GDPR_COUNTRIES as readonly string[]).includes(
    countryCode.toUpperCase(),
  );
};

/**
 * countryCode가 배너 생략 가능 지역인지 판단.
 * 값 없으면 false (배너 노출이 안전 default).
 */
export const isNoBannerCountry = (countryCode?: string | null): boolean => {
  if (!countryCode) return false;
  return (NO_BANNER_COUNTRIES as readonly string[]).includes(
    countryCode.toUpperCase(),
  );
};

export const COUNTRY_COOKIE_NAME = "bhap_country";
